/**
 * @author Charles Blais <charles.blais@nrcan-rncan.gc.ca>
 */
import { useEffect, useState } from 'react';

import { Alert } from 'react-bootstrap';

import { useAppState } from '../../contexts/app-state-provider';

import { useLanguageState } from '../../contexts/language-state-provider';

import useWaveformCache from '../../hooks/use-waveform-cache';
import { WaveformTrace } from '../../typings/waveform-plot';

import TracePlot, { TraceProps } from './trace-plot';


type Props = {
  sncl: string,
  period?: '1m' | '10m' | '1h',
  fetchInterval?: number,
} & TraceProps

// Create converter for period to query
const period2cache = {
  '1m': '5m',
  '10m': '15m',
  '1h': '1h',
}

/**
 * Ge the base url according to configuration file
 * @param url 
 * @returns 
 */
const getBaseUrl = (url: string): string => {
  const parts = url.split('/')
  parts.pop()
  return parts.join('/')
}


const TraceLivePlot = ({
  sncl,
  period = '1m',
  fetchInterval = undefined,
  ...props
}: Props) => {
  const [ state ] = useAppState();
  const [ lang ] = useLanguageState();

  // We create a local trace for the object to clear it from memory whenever
  // the SNCL or period properties change in the component.  We this, the setInterval
  // of the TracePlot component should unmount.
  const [ trace, setTrace ] = useState<WaveformTrace | undefined>();

  const { trace: data, loading, error, setUrl, setSNCL, setPeriod, refetch } = useWaveformCache({
    initialUrl: getBaseUrl(state.url),
    initialSNCL: sncl,
    initialPeriod: period2cache[period],
  });

  useEffect(() => {
    if(state.url) setUrl(getBaseUrl(state.url))
  }, [state.url, setUrl])

  useEffect(() => {
    if(sncl) {
      setTrace(undefined)
      setSNCL(sncl)
    }
  }, [sncl, setSNCL])

  useEffect(() => {
    if(period) {
      setTrace(undefined)
      setPeriod(period2cache[period])
    }
  }, [period, setPeriod])

  useEffect(() => {
    setTrace(data)
  }, [data])

  useEffect(() => {
    // set an update based on the fetch interval
    let myInterval: NodeJS.Timer | undefined = undefined;
    if(fetchInterval)
      myInterval = setInterval(refetch, fetchInterval*1000)
    // clear the interval if its already set and fetch interval undefined
    if(!fetchInterval && myInterval)
      clearInterval(myInterval)
    return () => {
      if(myInterval)
        clearInterval(myInterval)
    }
  }, [refetch, fetchInterval]);

  if(loading && !trace) {
    return <Alert bsStyle='info'><p>{lang.loading.title}</p></Alert>
  }

  if(error && !trace) {
    return (
      <Alert bsStyle="danger">
        <p>{lang.error.waveform}</p>
      </Alert>
    )
  }

  if(!trace)
    return null;

  return (
    <TracePlot
      {...props}
      trace={trace}
    />
  );
}

export default TraceLivePlot;
